import { useEffect, useRef, useState } from 'react'

import {
  DSAlert,
  DSButton,
  DSCol,
  DSIcon,
  DSModal,
  DSRow,
  DSText
} from '@vr/ds-react'

import ModalError from '@/components/modal-error'
import ModalSuccess from '@/components/modal-success'
import { useUnitCollectDownloadOptions } from '@/hooks/use-unit-collect-download'
import { useUnitCollectUploadOptions } from '@/hooks/use-unit-collect-upload'
import { downloadFile } from '@/utils/download-file'

const ModalFiles = ({
  onClose = () => {},
  isOpen,
  collectId
}: {
  onClose?: () => void
  isOpen: boolean
  collectId: string
}) => {
  const [fileUploaded, setFileUploaded] = useState(null)
  const [errorUploaded, setErrorUploaded] = useState(null)
  const [errorDownload, setErrorDownload] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { uploadOptions, isLoading: isLoadingUpload } =
    useUnitCollectUploadOptions()
  const { downloadOptions, isLoading: isLoadingDownload } =
    useUnitCollectDownloadOptions()

  const handleButtonClick = () => {
    fileInputRef.current.click()
  }

  const handleUploadFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setErrorUploaded(null)
    setFileUploaded(null)
    const file = event.currentTarget.files?.[0]
    if (file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedExtensions = ['xls', 'xlsx']
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(fileExtension)) {
        setErrorUploaded(
          'O arquivo selecionado deve ser do tipo .xls ou .xlsx.'
        )
        setFileUploaded(null)
        return
      }
      if (file.size > maxSize) {
        setErrorUploaded(
          'O arquivo selecionado excede o tamanho máximo permitido de 10MB.'
        )
        setFileUploaded(null)
        return
      }

      setFileUploaded(file)
    }
  }

  const payload = {
    collectId: collectId,
    fileUploaded: fileUploaded
  }

  useEffect(() => {
    const uploadFileAsync = async () => {
      if (!fileUploaded) return
      const response = await uploadOptions(
        payload.fileUploaded,
        payload.collectId
      )
      if (!response?.sucesso) {
        setShowErrorModal(response.erro || 'Erro ao enviar o arquivo.')
        onClose()
        setErrorUploaded(null)
        setFileUploaded(null)
        return
      }

      setShowSuccessModal(true)
      onClose()
      setErrorUploaded(null)
      setFileUploaded(null)
    }
    uploadFileAsync()
  }, [fileUploaded])

  const handleDownloadFile = async () => {
    const response = await downloadOptions(collectId)

    if (!response?.sucesso) {
      setErrorDownload(response?.erro || 'Erro ao baixar o arquivo modelo.')
      setTimeout(() => {
        setErrorDownload(null)
      }, 3000)
      return
    }

    downloadFile(response?.conteudo, `modelo_vinculo_cartoes.xlsx`, 'base64')

    onClose()
    setErrorDownload(null)
  }

  return (
    <>
      <DSModal config={{ show: isOpen, onClose }} css={{ width: '900px' }}>
        <DSModal.Header onClose={onClose}>
          Vincular cartões por arquivo
        </DSModal.Header>
        <DSModal.Body>
          <DSRow wrap='wrap' align='stretch' direction='row'>
            <DSCol
              xs={12}
              lg={6}
              gap={4}
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <DSIcon name='principal-download' size='extra' />
              <DSText as='p' weight='semibold' size={'lg'}>
                Baixar planilha
              </DSText>
              <DSText as='p' size={'md'} align='center'>
                Baixe o arquivo modelo em Excel e preencha com os números dos
                cartões que deseja vincular aos colaboradores.
              </DSText>
              {errorDownload && (
                <DSAlert
                  type='error'
                  hiddenBtnClose
                  css={{ padding: '2px 10px' }}
                >
                  {errorDownload}
                </DSAlert>
              )}
              <DSButton
                variant='secondary'
                onClick={handleDownloadFile}
                id='download-model-file-button'
                data-testid='download-model-file-button'
                disabled={isLoadingDownload}
                isLoading={isLoadingDownload}
                iconLeft={'principal-download'}
              >
                Baixar arquivo modelo
              </DSButton>
            </DSCol>

            <DSCol
              xs={12}
              lg={6}
              gap={4}
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <DSIcon name='principal-upload' size='extra' />
              <DSText as='p' weight='semibold' size={'lg'}>
                Faça upload do arquivo preenchido
              </DSText>
              <DSText as='p' size={'md'} align='center'>
                Selecione o arquivo Excel com os dados preenchidos.
              </DSText>
              <div className='self-center'>
                {fileUploaded && (
                  <DSAlert
                    type='success'
                    hiddenBtnClose
                    css={{ padding: '2px 10px' }}
                  >
                    {fileUploaded?.name} -{' '}
                    {Math.round(fileUploaded.size / 1024)} KB
                  </DSAlert>
                )}
                {errorUploaded && (
                  <DSAlert
                    type='error'
                    hiddenBtnClose
                    css={{ padding: '2px 10px' }}
                  >
                    {errorUploaded}
                  </DSAlert>
                )}
              </div>
              <DSButton
                variant='secondary'
                onClick={handleButtonClick}
                id='upload-model-file-button'
                data-testid='upload-model-file-button'
                disabled={isLoadingUpload}
                isLoading={isLoadingUpload}
                iconLeft={'principal-upload'}
              >
                Escolher arquivo
              </DSButton>
              <input
                type='file'
                onChange={handleUploadFile}
                accept='.xls,.xlsx'
                multiple={false}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
            </DSCol>
          </DSRow>
        </DSModal.Body>
        <DSModal.Footer></DSModal.Footer>
      </DSModal>

      <ModalSuccess
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title='Arquivo enviado com sucesso!'
      >
        Seu arquivo foi enviado para o processamento.
      </ModalSuccess>

      <ModalError
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title='Erro no envio do arquivo!'
      >
        {showErrorModal}
      </ModalError>
    </>
  )
}
export default ModalFiles
